import React, {
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
} from 'react';
import { useAuth } from '../../context/AuthContext';
import './ContactSubmissions.css';

const CONTACT_API_URL = 'https://api.avtechbmt.cloud/api/contact';
const getToken = (token) => token || localStorage.getItem('token');

const formatDate = (iso) => {
  const d = new Date(iso);
  return d.toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
};

const formatRelative = (iso) => {
  const diff = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 30) return `${days}d ago`;
  return formatDate(iso);
};

export default function ContactSubmissions() {
  const { token } = useAuth();

  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [query, setQuery] = useState('');
  const [unreadOnly, setUnreadOnly] = useState(false);
  const [selectedId, setSelectedId] = useState(null);
  const [readIds, setReadIds] = useState(() => new Set());
  const [copied, setCopied] = useState('');

  const abortRef = useRef(null);

  /* ------------------------------ Fetch data ------------------------------- */
  const fetchMessages = useCallback(async () => {
    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    setLoading(true);
    setError('');

    try {
      const res = await fetch(`${CONTACT_API_URL}/all`, {
        headers: { Authorization: `Bearer ${getToken(token)}` },
        signal: controller.signal,
      });

      const contentType = res.headers.get('content-type') || '';
      if (!contentType.includes('application/json')) {
        throw new Error(`Server returned non-JSON response (${res.status}).`);
      }

      const data = await res.json();
      if (!res.ok) throw new Error(data.message || 'Failed to fetch messages');

      const list = data.data || [];
      setMessages(list);
      setSelectedId((prev) => prev ?? list[0]?._id ?? null);
    } catch (err) {
      if (err.name !== 'AbortError') {
        setError(err.message || 'Unable to load inquiries.');
      }
    } finally {
      if (!controller.signal.aborted) setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchMessages();
    return () => abortRef.current?.abort();
  }, [fetchMessages]);

  /* ---------------------------- Derived values ---------------------------- */
  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return messages.filter((m) => {
      const matchesQuery =
        !q ||
        m.name?.toLowerCase().includes(q) ||
        m.email?.toLowerCase().includes(q) ||
        m.subject?.toLowerCase().includes(q) ||
        m.message?.toLowerCase().includes(q);
      const matchesUnread = !unreadOnly || !readIds.has(m._id);
      return matchesQuery && matchesUnread;
    });
  }, [messages, query, unreadOnly, readIds]);

  const selected = useMemo(
    () => filtered.find((m) => m._id === selectedId) || null,
    [filtered, selectedId]
  );

  const unreadCount = messages.length - readIds.size;

  /* ------------------------------- Handlers ------------------------------- */
  const openMessage = useCallback((id) => {
    setSelectedId(id);
    setReadIds((prev) => {
      if (prev.has(id)) return prev;
      const next = new Set(prev);
      next.add(id);
      return next;
    });
  }, []);

  const copyToClipboard = useCallback(async (text, label) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(label);
      setTimeout(() => setCopied(''), 1500);
    } catch {
      /* clipboard unavailable */
    }
  }, []);

  /* --------------------------------- Render -------------------------------- */
  return (
    <div className="contact-submissions-container">
      {/* Header */}
      <header className="contact-submissions-header">
        <div>
          <span className="contact-submissions-eyebrow">Inbox</span>
          <h2 className="contact-submissions-title">
            Contact Inquiries
            <span className="contact-submissions-count">
              {messages.length}
            </span>
            {unreadCount > 0 && (
              <span className="contact-submissions-unread-pill">
                {unreadCount} new
              </span>
            )}
          </h2>
        </div>
        <button
          className="contact-submissions-refresh-btn"
          onClick={fetchMessages}
          disabled={loading}
          aria-label="Refresh contact inquiries"
        >
          <span aria-hidden="true">🔄</span>
          <span>{loading ? 'Refreshing…' : 'Refresh'}</span>
        </button>
      </header>

      {/* Toolbar */}
      <div className="contact-submissions-toolbar">
        <input
          type="search"
          className="contact-submissions-search"
          placeholder="Search name, email, subject, or message…"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          aria-label="Search inquiries"
        />
        <label className="contact-submissions-toggle">
          <input
            type="checkbox"
            checked={unreadOnly}
            onChange={(e) => setUnreadOnly(e.target.checked)}
          />
          <span>Unread only</span>
        </label>
      </div>

      {/* Alerts */}
      {error && (
        <div className="contact-submissions-alert-error" role="alert">
          ⚠️ {error}
        </div>
      )}

      {/* Content */}
      {loading ? (
        <SkeletonList />
      ) : filtered.length === 0 ? (
        <p className="contact-submissions-no-data">
          {messages.length === 0
            ? 'No contact submissions found.'
            : 'No inquiries match your filters.'}
        </p>
      ) : (
        <div className="contact-submissions-layout">
          {/* ----------------------------- List pane ---------------------------- */}
          <ul
            className="contact-submissions-list"
            role="listbox"
            aria-label="Contact inquiries"
          >
            {filtered.map((item) => {
              const isSelected = item._id === selectedId;
              const isUnread = !readIds.has(item._id);
              return (
                <li key={item._id} role="option" aria-selected={isSelected}>
                  <button
                    type="button"
                    className={`contact-submissions-card ${
                      isSelected ? 'is-selected' : ''
                    }`}
                    onClick={() => openMessage(item._id)}
                  >
                    <div className="contact-submissions-card-top">
                      <span
                        className={`contact-submissions-dot ${
                          isUnread ? 'is-unread' : ''
                        }`}
                        aria-label={isUnread ? 'Unread' : 'Read'}
                      />
                      <strong className="contact-submissions-name">
                        {item.name || 'Unknown'}
                      </strong>
                      <time
                        className="contact-submissions-time"
                        dateTime={item.createdAt}
                      >
                        {formatRelative(item.createdAt)}
                      </time>
                    </div>

                    <p className="contact-submissions-card-subject">
                      {item.subject || '(no subject)'}
                    </p>

                    <p className="contact-submissions-card-preview">
                      {item.message}
                    </p>

                    <span className="contact-submissions-card-email">
                      {item.email}
                    </span>
                  </button>
                </li>
              );
            })}
          </ul>

          {/* ---------------------------- Detail pane --------------------------- */}
          {selected ? (
            <article
              className="contact-submissions-detail"
              aria-label="Message detail"
            >
              <div className="contact-submissions-detail-head">
                <h3 className="contact-submissions-detail-subject">
                  {selected.subject || '(no subject)'}
                </h3>
                <button
                  type="button"
                  className="contact-submissions-close-detail"
                  onClick={() => setSelectedId(null)}
                  aria-label="Close detail view"
                >
                  ✕
                </button>
              </div>

              <div className="contact-submissions-detail-meta">
                <div className="contact-submissions-avatar" aria-hidden="true">
                  {(selected.name || '?').charAt(0).toUpperCase()}
                </div>
                <div className="contact-submissions-detail-identity">
                  <strong>{selected.name || 'Unknown'}</strong>
                  <span className="contact-submissions-detail-date">
                    {formatDate(selected.createdAt)} ·{' '}
                    {formatRelative(selected.createdAt)}
                  </span>
                </div>
              </div>

              <div className="contact-submissions-detail-contacts">
                <ContactRow
                  label="Email"
                  value={selected.email}
                  onCopy={() =>
                    copyToClipboard(selected.email, 'email')
                  }
                  copied={copied === 'email'}
                  href={`mailto:${selected.email}`}
                />
                {selected.phone && (
                  <ContactRow
                    label="Phone"
                    value={selected.phone}
                    onCopy={() =>
                      copyToClipboard(selected.phone, 'phone')
                    }
                    copied={copied === 'phone'}
                    href={`tel:${selected.phone}`}
                  />
                )}
              </div>

              <div className="contact-submissions-detail-body">
                {selected.message}
              </div>

              <div className="contact-submissions-detail-actions">
                <a
                  className="contact-submissions-action-btn primary"
                  href={`mailto:${selected.email}?subject=Re: ${
                    selected.subject || ''
                  }`}
                >
                  ✉️ Reply
                </a>
                {selected.phone && (
                  <a
                    className="contact-submissions-action-btn"
                    href={`tel:${selected.phone}`}
                  >
                    📞 Call
                  </a>
                )}
              </div>
            </article>
          ) : (
            <div className="contact-submissions-detail-empty">
              <p>Select an inquiry to read the full message.</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/* ---------------------------- Subcomponents -------------------------------- */

function ContactRow({ label, value, onCopy, copied, href }) {
  return (
    <div className="contact-submissions-contact-row">
      <span className="contact-submissions-contact-label">{label}</span>
      <div className="contact-submissions-contact-value">
        <a href={href} className="contact-submissions-contact-link">
          {value}
        </a>
        <button
          type="button"
          className="contact-submissions-copy-btn"
          onClick={onCopy}
          aria-label={`Copy ${label.toLowerCase()}`}
        >
          {copied ? '✓ Copied' : 'Copy'}
        </button>
      </div>
    </div>
  );
}

function SkeletonList() {
  return (
    <ul className="contact-submissions-list" aria-hidden="true">
      {Array.from({ length: 5 }).map((_, i) => (
        <li key={i} className="contact-submissions-card is-skeleton">
          <div className="contact-submissions-skel-line short" />
          <div className="contact-submissions-skel-line wide" />
          <div className="contact-submissions-skel-line" />
          <div className="contact-submissions-skel-line narrow" />
        </li>
      ))}
    </ul>
  );
}